import cn from 'classnames'

import { HTMLProps } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/data-display/tooltip/Tooltip'
import { IconInfo } from '~/components/icons/IconInfo'
import { ClientOnly } from '@tanstack/react-router'

interface HomepageAboutProps extends HTMLProps<HTMLDivElement> {}

export function HomepageAbout ({
    className,
    ...props
}: HomepageAboutProps)
{
    const items = [{
        title: 'Privacy technology',
        text: 'We analyze encryption methods, protocols, and transaction obfuscation (e.g., zk-SNARKs, RingCT).',
        tooltip: 'At the moment, we don’t include CoinJoin projects built on top of transparent blockchains, as well as similar smart contracts.'
    }, {
        title: 'Real data protection',
        text: 'Only projects resistant to blockchain analysis and large-scale tracking are included.',
    }, {
        title: 'Reliable ecosystem',
        text: 'Coins with a total market cap over $100 million, a strong community, a transparent roadmap, and a proven security record.',
    }]

    return (
        <section id={'about'} className={cn('flex flex-col gap-y-8', 'xl:grid grid-cols-[1fr_2fr] gap-x-4', className)}>
            <HomepageAboutDiv
                className={cn(
                    'flex flex-col gap-5 justify-start items-start',
                    'px-4 py-6',
                    'md:px-10 md:py-4 md:gap-9',
                    'border border-accent/10',
                )}
            >
                <h2 className={cn('leading-[103%] uppercase')}>
                    <span aria-hidden={true}>[</span>About privacy.watch<span aria-hidden={true}>]</span>
                </h2>
                <p className={cn('text-balance text-accent/80 leading-[103%] uppercase')}>Private Cryptocurrencies&nbsp;— The Future of Finance</p>
                <p className={cn('font-light text-accent/80 tracking-tight leading-[normal] text-balance')}>
                    In an era of total control, private coins like Monero give users complete anonymity. Technologies like RingCT and zk-SNARKs make transaction tracking impossible, protecting personal data.

                    <br /><br />

                    With growing regulations, solutions where privacy is the default are in high demand. These coins have already proven their resilience and are becoming the foundation of a new decentralized economy
                </p>
            </HomepageAboutDiv>

            <HomepageAboutDiv className={cn('md:px-10 md:py-4', 'md:border md:border-accent/10')}>
                <h3
                    className={cn('text-start leading-[103%] text-balance font-medium uppercase')}
                >Our selection is based on three key principles:</h3>

                <ol
                    className={cn(
                        'mt-4',
                        'flex flex-col gap-2 justify-stretch  flex-wrap',
                        'md:mt-8',
                        'md:flex-row md:justify-start md:items-stretch md:gap-6',
                        '*:basis-0 *:grow-1 *:shrink-1 md:*:min-w-68',
                        'xl:*:min-w-57'
                    )}
                >
                    {items.map((item, index) => (
                        <HomepageAboutListItem
                            key={index}
                            index={index + 1} {...item}
                            className={cn('md:min-h-78')}
                        />
                    ))}
                </ol>
            </HomepageAboutDiv>
        </section>
    )
}

interface HomepageAboutDivProps extends HTMLProps<HTMLDivElement> {}

export function HomepageAboutDiv ({
    className,
    children,
    ...props
}: HomepageAboutDivProps)
{
    return (
        <div className={cn('rounded-lg', className)}>
            {children}
        </div>
    )
}

interface HomepageAboutListItemProps extends HTMLProps<HTMLLIElement>
{
    index: number
    title: string
    text: string
    tooltip?: string
}

function HomepageAboutListItem ({
    index,
    title,
    text,
    tooltip,
    className,
    ...props
}: HomepageAboutListItemProps)
{
    return (
        <li
            className={cn(
                'flex flex-col justify-between items-stretch gap-5',
                'px-4 py-6 rounded-lg',
                'md:gap-4',
                'md:px-5 md:py-6',
                'border border-dashed border-accent/40',
                'md:border-accent/40',
                className,
            )}
        >
            <p className={cn('order-3 font-medium text-accent/80')}>
                <span aria-hidden={true}>[</span>{index}<span aria-hidden={true}>]</span>
            </p>
            <h4 className={cn('order-1 font-medium uppercase text-accent/80 leading-[103%] text-balance')}>{title}</h4>
            <p className={cn('relative order-2 font-light text-accent/80 tracking-tight leading-[normal] text-balance')}>
                {text}

                { tooltip ? (
                    <ClientOnly>
                        <Tooltip>

                            <TooltipTrigger
                                className={cn('inline-block ms-1 align-baseline')}
                            > <span className={cn('sr-only')}>More info</span> <IconInfo
                                className={cn('w-3.5 h-3.5 text-accent/40')}
                            /> </TooltipTrigger>

                            <TooltipContent>
                                <div className={'tooltip w-[calc(100%-1rem)] max-w-prose'}>
                                    {tooltip}
                                </div>
                            </TooltipContent>

                        </Tooltip>
                    </ClientOnly>
                ) : null }
            </p>
        </li>
    )
}